<template>
  <v-dialog :value="value" max-width="640" :fullscreen="isMobile" scrollable @input="$emit('input', $event)">
    <div class="ll">
      <header class="ll__head">
        <span class="ll__icon"><v-icon color="primary">mdi-account-multiple-plus-outline</v-icon></span>
        <div class="ll__titles">
          <div class="ll__title">{{ $t('crmLinkExisting') }}</div>
          <div class="ll__sub">{{ $t('crmLinkExistingHint') }}</div>
        </div>
        <button type="button" class="ll__close" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></button>
      </header>

      <div class="ll__bar">
        <div class="ll__search">
          <v-icon small>mdi-magnify</v-icon>
          <input v-model="search" :placeholder="$t('crmSearchUsers')" @input="debounced">
        </div>
        <button type="button" class="ll__all" :disabled="!rows.length" @click="toggleAll">
          {{ allOn ? $t('crmUnselectAll') : $t('crmSelectAll') }}
        </button>
      </div>

      <div class="ll__body" @scroll="onScroll">
        <label v-for="u in rows" :key="u.id" class="ll__row" :class="{ on: selected.includes(u.id) }">
          <v-simple-checkbox :value="selected.includes(u.id)" :ripple="false" @input="toggle(u.id)" />
          <crm-avatar :name="name(u)" :size="30" />
          <div class="ll__main">
            <b>{{ name(u) }}</b>
            <small><span class="ll__ltr">{{ u.mobile }}</span>{{ u.business_name ? ' · ' + u.business_name : '' }}</small>
          </div>
        </label>
        <div v-if="loading" class="ll__loading"><v-progress-circular :size="20" :width="2" indeterminate color="primary" /></div>
        <div v-else-if="!rows.length" class="ll__empty">{{ $t('crmNoUserFound') }}</div>
      </div>

      <div class="ll__opts">
        <crm-user-picker v-model="opts.owner_user_id" title="crmOwner" />
        <v-select v-model="opts.source_id" :items="sourceItems" :label="$t('crmSource')" outlined dense hide-details clearable />
        <v-select v-model="opts.status" :items="statusItems" :label="$t('crmStatus')" outlined dense hide-details clearable />
        <v-select v-model="opts.priority" :items="priorityItems" :label="$t('crmPriority')" outlined dense hide-details />
      </div>

      <footer class="ll__foot">
        <v-btn color="primary" depressed class="app-form-action-btn" :disabled="!selected.length" :loading="saving" @click="save">
          {{ $t('crmAddCount', { n: selected.length }) }}
        </v-btn>
        <v-btn color="grey" outlined class="app-form-action-btn" @click="$emit('input', false)">{{ $t('crmCancel') }}</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import api, { crmError } from '../crmApi'
import { crm, pick, personName, leadStatuses, PRIORITIES } from '../crmStore'
import CrmUserPicker from './CrmUserPicker'
import { CrmAvatar } from './CrmBits'

export default {
  name: 'CrmLinkLeadsDialog',
  components: { CrmUserPicker, CrmAvatar },
  inject: ['shell'],
  props: { value: Boolean },
  data: () => ({ rows: [], selected: [], search: '', page: 1, lastPage: 1, loading: false, saving: false, timer: null, opts: {} }),
  computed: {
    allOn () { return this.rows.length && this.rows.every(r => this.selected.includes(r.id)) },
    sourceItems () { return crm.sources.map(s => ({ value: s.id, text: s.title })) },
    statusItems () { return leadStatuses().filter(s => s.category === 'open').map(s => ({ value: s.key, text: s.title || this.$t('crmStatus_' + s.key) })) },
    priorityItems () { return PRIORITIES.map(p => ({ value: p, text: this.$t('crmPriority_' + p) })) }
  },
  watch: {
    value (v) {
      if (!v) return
      this.selected = []
      this.search = ''
      this.opts = { owner_user_id: crm.me ? crm.me.user_id : null, source_id: null, status: null, priority: 'normal' }
      this.reload()
    }
  },
  methods: {
    name (u) { return personName(u) || u.name || u.business_name || u.mobile || '#' + u.id },
    debounced () {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.reload, 350)
    },
    reload () {
      this.page = 1
      this.rows = []
      this.load()
    },
    async load () {
      this.loading = true
      try {
        const res = await api.leadsAvailable({ search: this.search || undefined, page: this.page, itemsPerPage: 40 })
        const items = pick(res)
        this.rows = this.page === 1 ? items : this.rows.concat(items)
        this.lastPage = res.last_page || 1
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    onScroll (e) {
      const el = e.target
      if (!this.loading && this.page < this.lastPage && el.scrollTop + el.clientHeight > el.scrollHeight - 120) {
        this.page++
        this.load()
      }
    },
    toggle (id) {
      this.selected = this.selected.includes(id) ? this.selected.filter(x => x !== id) : this.selected.concat(id)
    },
    toggleAll () {
      const ids = this.rows.map(r => r.id)
      this.selected = this.allOn ? this.selected.filter(id => !ids.includes(id)) : [...new Set(this.selected.concat(ids))]
    },
    async save () {
      this.saving = true
      try {
        const body = { user_ids: this.selected }
        Object.keys(this.opts).forEach(k => { if (this.opts[k] !== null && this.opts[k] !== '') body[k] = this.opts[k] })
        const res = await api.linkLeads(body)
        const created = (res && res.created) || []
        const skipped = (res && res.skipped) || []
        this.shell.toast(this.$t('crmLinked', { n: created.length }) + (skipped.length ? ' · ' + this.$t('crmSkipped', { n: skipped.length }) : ''))
        this.shell.$emit('changed', { type: 'lead', record: null })
        this.$emit('input', false)
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.ll { background: #fff; border-radius: 6px; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh; }
.theme--dark .ll { background: #1e1e1e; }
.ll__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.ll__icon { width: 40px; height: 40px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; }
.ll__titles { flex: 1; min-width: 0; }
.ll__title { font-weight: 700; }
.ll__sub { font-size: .76rem; opacity: .6; }
.ll__close { width: 36px; height: 36px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: flex; align-items: center; justify-content: center; }
.ll__bar { display: flex; align-items: center; gap: 10px; padding: 12px 18px; flex-shrink: 0; }
.ll__search { flex: 1; display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .12); }
.ll__search:focus-within { border-color: var(--v-primary-base); }
.ll__search input { flex: 1; outline: none; font-size: .85rem; color: inherit; }
.ll__all { font-size: .78rem; font-weight: 600; color: var(--v-primary-base); white-space: nowrap; }
.ll__body { flex: 1; min-height: 200px; overflow-y: auto; padding: 0 18px 8px; }
.ll__row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 6px; cursor: pointer; margin-bottom: 2px; }
.ll__row:hover { background: rgba(127, 127, 127, .06); }
.ll__row.on { background: rgba(25, 118, 210, .07); }
.ll__row ::v-deep .v-simple-checkbox { margin: 0; }
.ll__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ll__main b { font-size: .86rem; }
.ll__main small { font-size: .74rem; opacity: .65; }
.ll__ltr { direction: ltr; display: inline-block; }
.ll__loading, .ll__empty { display: flex; justify-content: center; padding: 18px; font-size: .82rem; opacity: .7; }
.ll__opts { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 14px 18px; border-top: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.ll__foot { display: flex; gap: 10px; padding: 12px 18px; background: #fafbfd; border-top: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.theme--dark .ll__foot { background: #232323; }
.ll ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.ll ::v-deep fieldset { border-radius: 6px; }
@media (max-width: 799px) {
  .ll { max-height: none; height: 100%; border-radius: 0; }
  .ll__opts { grid-template-columns: 1fr; }
}
</style>
